// src/contexts/AuthContext.tsx

import {
  createContext,
  useContext,
  useEffect,
  useState,
  useMemo,
  useCallback,
  type ReactNode,
} from 'react';
import { supabase } from '../lib/supabaseClient';
import { SUPABASE_AUTH_STORAGE_KEY } from '../lib/supabaseClient';
import type { Session, AuthError, Provider, User } from '@supabase/supabase-js';

// Define user metadata interface
interface RCNUserMetadata {
  is_admin?: boolean;
  is_superuser?: boolean;
  [key: string]: unknown;
}

// Utility functions to get role and privileges
function getAppRole(user: User | null): string | null {
  return user?.app_metadata?.role ?? null;
}
function getIsSuperuser(user: User | null): boolean {
  return (user?.user_metadata as RCNUserMetadata)?.is_superuser === true;
}
function getIsAdmin(user: User | null): boolean {
  return (user?.user_metadata as RCNUserMetadata)?.is_admin === true;
}

// Define the shape of our auth context
interface AuthContextType {
  user: User | null;
  session: Session | null;
  loading: boolean;
  signUp: (
    email: string,
    password: string,
  ) => Promise<{ user: User | null; error: AuthError | null }>;
  signIn: (
    email: string,
    password: string,
  ) => Promise<{ session: Session | null; error: AuthError | null }>;
  signOut: () => Promise<{ error: AuthError | null }>;
  signInWithProvider: (provider: Provider) => Promise<{
    user: User | null;
    session: Session | null;
    error: AuthError | null;
  }>;
  signInWithGithub: () => Promise<{
    user: User | null;
    session: Session | null;
    error: AuthError | null;
  }>;
  resetPassword: (email: string) => Promise<{
    data: Record<string, unknown> | null;
    error: AuthError | null;
  }>;
  updateProfile: (
    updates: Partial<User>,
  ) => Promise<{ data: User | null; error: AuthError | null }>;
  isAdmin: boolean;
  isSuperuser: boolean;
  role: string | null;
}

// Create context with undefined default
const AuthContext = createContext<AuthContextType | undefined>(undefined);

// AuthProvider component
export const AuthProvider = ({ children }: { children: ReactNode }) => {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState<boolean>(true);

  // On mount, check current auth state and subscribe to changes
  useEffect(() => {
    let isMounted = true;
    console.log('🔄 Starting auth session check...');
    // Diagnostic: list localStorage auth keys present at mount
    try {
      const lsKeys = Object.keys(window.localStorage || {}).filter((k) =>
        k.startsWith('sb-'),
      );
      console.log('🗝️  Supabase auth-related localStorage keys:', lsKeys);
    } catch (e) {
      console.warn('Cannot inspect localStorage keys', e);
    }

    // 1. EARLY HYDRATION (synchronous) — prevents UI flash / logout feeling
    let earlyHydrated = false;
    if (SUPABASE_AUTH_STORAGE_KEY) {
      try {
        const raw = localStorage.getItem(SUPABASE_AUTH_STORAGE_KEY);
        if (raw) {
          const parsed = JSON.parse(raw);
          const stored = parsed?.currentSession;
          if (stored?.user && stored?.access_token) {
            earlyHydrated = true;
            setSession(stored as unknown as Session);
            setUser(stored.user as User);
            // Keep loading true briefly: we'll confirm with official API below
            console.log(
              '⚡ Early session hydration from localStorage (pre getSession)',
            );
          }
        }
      } catch (e) {
        console.warn('Early hydration failed', e);
      }
    }

    // Add timeout to prevent hanging on getSession
    const authTimeout = setTimeout(() => {
      if (isMounted) {
        if (earlyHydrated) {
          console.log(
            '⏱️ getSession still pending after timeout, using early hydrated session',
          );
        } else {
          console.warn(
            '⚠️ Auth session check timed out and no early session found',
          );
        }
        setLoading(false);
      }
    }, 4000); // shorter timeout since we have early hydration

    // Get initial session
    console.log('📡 Calling supabase.auth.getSession()...');
    const startTime = Date.now();

    supabase.auth
      .getSession()
      .then(
        async ({
          data: { session },
        }: {
          data: { session: Session | null };
        }) => {
          const elapsed = Date.now() - startTime;
          if (session) {
            console.log(`✅ Auth session loaded successfully in ${elapsed}ms`);
          } else {
            let hasStoredAuthPayload = false;
            if (SUPABASE_AUTH_STORAGE_KEY) {
              try {
                hasStoredAuthPayload = Boolean(
                  localStorage.getItem(SUPABASE_AUTH_STORAGE_KEY),
                );
              } catch {
                hasStoredAuthPayload = false;
              }
            }

            if (hasStoredAuthPayload) {
              console.log(
                `ℹ️  No session from getSession() after ${elapsed}ms – attempting refreshSession() fallback...`,
              );
              try {
                const { data, error } = await supabase.auth.refreshSession();
                if (error) {
                  console.info('refreshSession skipped:', error.message);
                } else if (data.session) {
                  console.log('🔁 Session recovered via refreshSession()');
                  session = data.session; // reassign for below state update
                } else {
                  console.log('🔁 refreshSession() returned no session');
                }
              } catch (e) {
                console.info('refreshSession skipped:', e);
              }
            } else {
              console.log(`ℹ️  No stored auth session after ${elapsed}ms`);
            }
            // Manual localStorage inspection fallback
            if (!session && SUPABASE_AUTH_STORAGE_KEY) {
              try {
                const raw = localStorage.getItem(SUPABASE_AUTH_STORAGE_KEY);
                if (raw) {
                  console.log(
                    '🧪 Found stored auth payload, attempting manual rehydration',
                  );
                  const parsed = JSON.parse(raw);
                  const stored = parsed?.currentSession;
                  if (stored?.access_token && stored?.refresh_token) {
                    try {
                      const { data, error } = await supabase.auth.setSession({
                        access_token: stored.access_token,
                        refresh_token: stored.refresh_token,
                      });
                      if (error) {
                        console.warn(
                          '⚠️  setSession() error during manual rehydrate',
                          error.message,
                        );
                      } else if (data.session) {
                        console.log(
                          '✅ Manual rehydrate via setSession() succeeded',
                        );
                        session = data.session;
                      }
                    } catch (e) {
                      console.warn(
                        '⚠️  setSession() threw during manual rehydrate',
                        e,
                      );
                    }
                  } else {
                    console.log('ℹ️  Stored auth payload missing tokens');
                  }
                } else {
                  console.log(
                    'ℹ️  No localStorage auth payload present for key',
                    SUPABASE_AUTH_STORAGE_KEY,
                  );
                }
              } catch (e) {
                console.warn('Manual rehydrate inspection failed', e);
              }
            }
          }

          if (isMounted) {
            clearTimeout(authTimeout);
            if (session) {
              setSession(session);
              setUser(session.user);
            } else if (!earlyHydrated) {
              setSession(null);
              setUser(null);
            }
            setLoading(false);
            if (session) {
              console.log('👤 User authenticated:', session.user.email);
            } else {
              console.log('👤 Still no active session after fallback attempts');
            }
          }
        },
      )
      .catch((error) => {
        const elapsed = Date.now() - startTime;
        console.error(
          `❌ Auth session loading failed after ${elapsed}ms:`,
          error,
        );
        if (isMounted) {
          clearTimeout(authTimeout);
          if (!earlyHydrated) setLoading(false);
        }
      });

    // Second-chance recheck after 1s in case of race with storage restoration
    const secondChance = setTimeout(async () => {
      if (!isMounted || !loading) return;
      try {
        console.log('⏱️ Performing delayed second-chance getSession()');
        const {
          data: { session: lateSession },
        } = await supabase.auth.getSession();
        if (lateSession && isMounted) {
          console.log('✅ Late session detected on second-chance attempt');
          setSession(lateSession);
          setUser(lateSession.user);
          setLoading(false);
        }
      } catch (e) {
        console.warn('Second-chance getSession() failed', e);
      }
    }, 1000);

    // Listen to auth changes
    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange(
      (event: string, session: Session | null) => {
        console.log(
          '🔄 Auth state changed:',
          event,
          session?.user?.email || 'No user',
        );
        if (!isMounted) return;
        setSession(session);
        setUser(session?.user ?? null);
        // In some edge cases INITIAL_SESSION may arrive after our timeout fallback
        if (event === 'INITIAL_SESSION' && loading) {
          console.log('🟢 INITIAL_SESSION event resolving loading state');
          setLoading(false);
        }
      },
    );

    return () => {
      isMounted = false;
      clearTimeout(authTimeout);
      subscription.unsubscribe();
      clearTimeout(secondChance);
    };
  }, []);

  // Auth actions
  const signUp = useCallback(async (email: string, password: string) => {
    const { data, error } = await supabase.auth.signUp({
      email,
      password,
    });
    return { user: data.user, error };
  }, []);

  const signIn = useCallback(async (email: string, password: string) => {
    const { data, error } = await supabase.auth.signInWithPassword({
      email,
      password,
    });
    return { session: data.session, error };
  }, []);

  const signOut = useCallback(async () => {
    // Attempt to remove push subscription server record before sign out
    try {
      if ('serviceWorker' in navigator) {
        const reg = await navigator.serviceWorker.ready;
        const sub = await reg.pushManager.getSubscription();
        if (sub) {
          const endpoint = sub.endpoint;
          try {
            await sub.unsubscribe();
          } catch {
            /* ignore */
          }
          import('../services/pushSubscriptionService').then((m) =>
            m.removeSubscription(endpoint),
          );
        }
      }
    } catch (e) {
      console.warn('Push subscription cleanup failed on signOut', e);
    }
    const { error } = await supabase.auth.signOut();
    return { error };
  }, []);

  const signInWithProvider = useCallback(async (provider: Provider) => {
    const { error } = await supabase.auth.signInWithOAuth({
      provider,
    });
    return { user: null, session: null, error };
  }, []);

  const signInWithGithub = useCallback(async () => {
    const { error } = await supabase.auth.signInWithOAuth({
      provider: 'github',
    });
    return { user: null, session: null, error };
  }, []);

  const resetPassword = useCallback(async (email: string) => {
    const { data, error } = await supabase.auth.resetPasswordForEmail(email);
    return { data, error };
  }, []);

  const updateProfile = useCallback(async (updates: Partial<User>) => {
    const { data, error } = await supabase.auth.updateUser({
      data: updates,
    });
    return { data: data.user, error };
  }, []);

  const value = useMemo<AuthContextType>(
    () => ({
      user,
      session,
      loading,
      signUp,
      signIn,
      signOut,
      signInWithProvider,
      signInWithGithub,
      resetPassword,
      updateProfile,
      isAdmin: getIsAdmin(user),
      isSuperuser: getIsSuperuser(user),
      role: getAppRole(user),
    }),
    [
      user,
      session,
      loading,
      signUp,
      signIn,
      signOut,
      signInWithProvider,
      signInWithGithub,
      resetPassword,
      updateProfile,
    ],
  );

  return (
    <AuthContext.Provider value={value}>
      {loading ? (
        <div className="flex flex-col items-center justify-center h-screen gap-4 text-sm">
          <div>Loading authentication...</div>
          <div className="text-xs text-gray-500 dark:text-gray-400">
            If this takes too long, there may be a network connectivity issue.
          </div>
        </div>
      ) : (
        children
      )}
    </AuthContext.Provider>
  );
};

// Custom hook for consuming auth context
export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
